import { Container } from '@ag1/ui';
import { useHeaderContext } from '@portal/state/context/HeaderContext';
import { routes } from 'constants/routes';
import type { InferGetStaticPropsType } from 'next';
import { languages } from 'schema/locale';
import type { PageTranslation } from 'schema/studio/studio';
import PageHeaderFooterLayout from 'shared/layouts/PageHeaderFooterLayout';
import SubscribeSection from 'shared/studio/SubscribeSection/SubscribeSection';
import { ToTopButton } from 'shared/ui/ToTopButton/ToTopButton';
import { urlFor } from 'shared/utils/urlFor';
import { FullEduHubArticleResult } from 'types/studio/eduHub';
import { HeaderBackground } from 'types/studio/header';
import { EduArticleBody } from './components/EduArticleBody';
import { EduArticleHero } from './components/EduArticleHero';
import { EduArticleImageAndText } from './components/EduArticleImageAndText';
import { EduArticleTags } from './components/EduArticleTags';
import type { getStaticProps } from './getStaticProps';

type AssumedProps = {
  article: FullEduHubArticleResult;
};

export const EduArticlePage = ({
  article,
  footerData,
  headerData: initialHeaderData,
  locale
}: InferGetStaticPropsType<typeof getStaticProps> & AssumedProps) => {
  const { getHeaderDataDefaults, useSetHeaderOptions } = useHeaderContext();
  useSetHeaderOptions(initialHeaderData);
  const headerData = getHeaderDataDefaults(initialHeaderData);

  const {
    headline,
    content,
    coverImage,
    translations,
    slug,
    addImageAndText = false,
    eduArticleCommonData,
    seo,
    tags,
    structuredSchemaData,
    canonical
  } = article;

  const getEmptyTranslations: PageTranslation[] = languages.map((language) =>
    language === locale
      ? {
          slug,
          language
        }
      : {
          slug: { _type: 'slug', current: routes.blog },
          language
        }
  );

  const getCoverImage = () => {
    const image = seo?.image || coverImage;
    return image ? urlFor(image)?.url : '';
  };

  const getHeadlineLinks = () => (
    <>
      <meta property="og:type" content="article" />
      <meta property="article:published_time" content={article.date} />
      <meta property="article:section" content={article.category.title} />
    </>
  );

  return (
    <PageHeaderFooterLayout
      footerData={footerData}
      headerData={headerData}
      title={seo?.title || headline || ''}
      description={article.seo?.description}
      hasProgressBar
      newThemeProgressBar
      coverImage={getCoverImage()}
      headerLinks={getHeadlineLinks()}
      headerBackground={HeaderBackground.white}
      pageTranslations={translations?.length ? translations : getEmptyTranslations}
      structuredSchemaData={structuredSchemaData}
      canonicalLinkData={canonical}>
      <Container narrow>
        <EduArticleHero article={article} />
        {content && <EduArticleBody content={content} />}
      </Container>
      {eduArticleCommonData?.toTopButton && (
        <ToTopButton toTopButton={eduArticleCommonData.toTopButton} theme="new" />
      )}
      {addImageAndText && eduArticleCommonData?.eduImageAndText && (
        <EduArticleImageAndText content={eduArticleCommonData?.eduImageAndText} />
      )}
      {tags && tags.length > 0 && <EduArticleTags tags={tags} />}
      {!!eduArticleCommonData?.showSubscribeSection && !!eduArticleCommonData?.subscribeSection && (
        <SubscribeSection
          _type="subscribeSection"
          headline={eduArticleCommonData.subscribeSection.headline}
          subtitle={eduArticleCommonData.subscribeSection.subtitle}
          placeholder={eduArticleCommonData.subscribeSection.placeholder}
          buttonLabel={eduArticleCommonData.subscribeSection.buttonLabel}
          successButtonLabel={eduArticleCommonData.subscribeSection.successButtonLabel}
          errorMessage={eduArticleCommonData.subscribeSection.errorMessage}
          theme={eduArticleCommonData.subscribeSection.theme}
          visibleOnPageLoad={false}
          _key="subscribeSection"
        />
      )}
    </PageHeaderFooterLayout>
  );
};
